import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Paper,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import styled from 'styled-components';
import { apiCall } from "../../services/ApiCall.js";
import config from "../../config/config.json";
import { useNavigate } from 'react-router-dom';

// Styled Components with #0d6efd theme
const StyledContainer = styled(Box)`
  padding: 24px;
 
  min-height: 80vh;
`;

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
box-shadow: 0px 2px 3.5px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid rgba(13, 110, 253, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0px 4px 7px rgba(0, 0, 0, 0.15); !important;
  }
`;

const MetricCard = styled(Paper)`
  padding: 24px;
  border-radius: 16px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%) !important;
  box-shadow: 0 4px 20px rgba(13, 110, 253, 0.1) !important;
  border: 1px solid rgba(13, 110, 253, 0.08);
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(13, 110, 253, 0.15) !important;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient};
  }
`;

const IconWrapper = styled(Box)`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.background};
  margin-bottom: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ChartContainer = styled(Box)`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(13, 110, 253, 0.08);
`;

const StyledSelect = styled(Select)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(13, 110, 253, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      border-color: #0d6efd;
    }
    
    &.Mui-focused {
      border-color: #0d6efd;
      box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
    }
  }
`;

const CompaniesOverview = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [companyData, setCompanyData] = useState([]);
  const [sitesData, setSitesData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedSitesYear, setSelectedSitesYear] = useState(new Date().getFullYear());
  const [selectedSitesMonth, setSelectedSitesMonth] = useState(new Date().getMonth());

  const getCompanyGraphData = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getCompanyGraphData`,
      {},
      {},
      "GET"
    );

    if (isSuccess) {
      setCompanyData(data?.data);
    }
  };

  const getSitesGraphData = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getSitesGraphData`,
      {},
      {},
      "GET"
    );

    if (isSuccess) {
      setSitesData(data?.data);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    }
    getCompanyGraphData();
    getSitesGraphData();
  }, []);

  // Enhanced chart options with #0d6efd theme
  const getChartOptions = (color) => ({
    chart: {
      type: 'area',
      toolbar: { show: false },
      sparkline: { enabled: true },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      }
    },
    stroke: { 
      curve: 'smooth', 
      width: 3,
      colors: [color]
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: [alpha(color, 0.1)],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    },
    colors: [color],
    tooltip: { 
      enabled: true,
      theme: 'light',
      style: {
        fontSize: '12px'
      }
    },
    grid: { show: false },
    xaxis: { 
      labels: { show: false }, 
      axisBorder: { show: false }, 
      axisTicks: { show: false } 
    },
    yaxis: { labels: { show: false } },
  });

  const filterCompanyData = () => {
    return companyData.filter(company => {
      const createdAt = new Date(company.createdAt);
      return (
        createdAt.getFullYear() === selectedYear &&
        createdAt.getMonth() === selectedMonth
      );
    });
  };

  const filteredCompanies = filterCompanyData();

  const getMonthlyCompanyData = () => {
    const monthlyData = Array(12).fill(0);
    companyData.forEach(company => {
      const createdAt = new Date(company.createdAt);
      if (createdAt.getFullYear() === selectedYear) {
        const month = createdAt.getMonth();
        monthlyData[month] += 1;
      }
    });
    return monthlyData;
  };

  const companiesChartSeries = [{ 
    name: 'Companies',
    data: getMonthlyCompanyData() 
  }];

  const filterSitesData = () => {
    return sitesData.filter(sites => {
      const createdAt = new Date(sites.createdAt);
      return (
        createdAt.getFullYear() === selectedSitesYear &&
        createdAt.getMonth() === selectedSitesMonth
      );
    });
  };

  const filteredSites = filterSitesData();

  const getMonthlySitesData = () => {
    const monthlyData = Array(12).fill(0);
    sitesData.forEach(site => {
      const createdAt = new Date(site.createdAt);
      if (createdAt.getFullYear() === selectedSitesYear) {
        const month = createdAt.getMonth();
        monthlyData[month] += 1;
      }
    });
    return monthlyData;
  };

  const sitesChartSeries = [{ 
    name: 'Sites',
    data: getMonthlySitesData() 
  }];

  const monthOptions = Array.from(
    { length: selectedYear === new Date().getFullYear() ? new Date().getMonth() + 1 : 12 },
    (_, index) => ({
      value: index,
      label: new Date(0, index).toLocaleString('default', { month: 'long' })
    })
  );

  const yearOptions = [...Array(5)].map((_, index) => {
    const year = new Date().getFullYear() - index;
    return { value: year, label: year.toString() };
  });

  return (
    <StyledContainer>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            background: '#333333',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'left',
            mb: 1,
          }}
        >
          Analytics Dashboard
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#64748b',
            textAlign: 'left'
          }}
        >
          Companies & Sites Overview
        </Typography>
      </Box>

      <Grid container spacing={3}>
      
        {/* Companies Chart */}
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardContent sx={{ p: 3 ,display: 'flex', flexDirection: 'column'}}>
              <Box sx={{display: 'flex', alignItems: 'center', mb: 3 ,flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center'}}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TrendingUpIcon sx={{ color: '#0d6efd', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                  Company Growth
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <StyledSelect
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {yearOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <StyledSelect
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  >
                    {monthOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection:'column',alignItems: 'start', mb: 2 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mr: 2 
                  }}
                >
                  {filteredCompanies.length || 0}
                </Typography>
                <Chip 
                  label={`of ${companyData.length || 0} total`}
                  size="small"
                  sx={{ 
                    background: alpha('#0d6efd', 0.1),
                    color: '#0d6efd',
                    fontWeight: 500
                  }}
                />
              </Box>

              <ChartContainer>
                <Chart 
                  options={getChartOptions('#0d6efd')} 
                  series={companiesChartSeries} 
                  type="area" 
                  height={120} 
                />
              </ChartContainer>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Sites Chart */}
        <Grid item xs={12} lg={6}>
          <StyledCard>
            <CardContent sx={{ p: 3 ,display: 'flex',flexDirection:'column'}}>
              <Box sx={{display: 'flex', alignItems: 'center', mb: 3 ,flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center'}}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AnalyticsIcon sx={{ color: '#084298', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                  Sites Growth
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <StyledSelect
                    value={selectedSitesYear}
                    onChange={(e) => setSelectedSitesYear(Number(e.target.value))}
                  >
                    {yearOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <StyledSelect
                    value={selectedSitesMonth}
                    onChange={(e) => setSelectedSitesMonth(Number(e.target.value))}
                  >
                    {monthOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection:'column',alignItems: 'start', mb: 2 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    background: 'linear-gradient(135deg, #084298 0%, #052c65 100%)',
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mr: 2 
                  }}
                >
                  {filteredSites.length || 0}
                </Typography>
                <Chip 
                  label={`of ${sitesData.length || 0} total`}
                  size="small"
                  sx={{ 
                    background: alpha('#F3E8FF', 0.1),
                    color: '#A855F7',
                    fontWeight: 500
                  }}
                />
              </Box>

              <ChartContainer>
                <Chart 
                  options={getChartOptions('#A855F7')} 
                  series={sitesChartSeries} 
                  type="area" 
                  height={120} 
                />
              </ChartContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default CompaniesOverview;